// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/tipProcessing.js
//
// Decodes candidate tip, grain and pattern sources into canvases the
// renderer uses. Each source application has its own tip convention:
//   Procreate  greyscale, white = paint ('light'; 'dark' when inverted)
//   Krita      PNG/SVG: dark = paint, alpha respected; GBR/GIH raw masks
//              (255 = paint); colour stamps keep their RGB ('image')
//   Photoshop  8-bit mask, 255 = paint ('alpha')
//   loose      decided from the image itself ('auto')
// Tip canvases hold the paint mask in alpha with black RGB, except colour
// stamps. Images are size-checked before decode and downscaled to
// MAX_TIP_SIZE; grains/patterns become power-of-two greyscale textures so
// WebGL1 can repeat them.

export const MAX_TIP_SIZE = 1024;
export const GRAIN_TEXTURE_SIZE = 512;
const MAX_DECODE_DIMENSION = 8192;
const MAX_DECODE_PIXELS = 48 * 1024 * 1024;

function createCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

function context2d(canvas) {
  return canvas.getContext('2d', { willReadFrequently: true });
}

function readU32Be(bytes, offset) {
  return ((bytes[offset] << 24) >>> 0) + (bytes[offset + 1] << 16) + (bytes[offset + 2] << 8) + bytes[offset + 3];
}

export function readImageDimensions(bytes) {
  if (bytes.length > 24 && bytes[0] === 137 && bytes[1] === 80) {
    return { width: readU32Be(bytes, 16), height: readU32Be(bytes, 20) };
  }
  if (bytes.length > 4 && bytes[0] === 0xff && bytes[1] === 0xd8) {
    let offset = 2;
    while (offset + 9 < bytes.length) {
      if (bytes[offset] !== 0xff) {
        offset += 1;
        continue;
      }
      const marker = bytes[offset + 1];
      const length = (bytes[offset + 2] << 8) | bytes[offset + 3];
      if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
        return { height: (bytes[offset + 5] << 8) | bytes[offset + 6], width: (bytes[offset + 7] << 8) | bytes[offset + 8] };
      }
      offset += 2 + length;
    }
  }
  return null;
}

function mimeOf(bytes) {
  if (bytes[0] === 137 && bytes[1] === 80) return 'image/png';
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return 'image/jpeg';
  if (bytes[0] === 0x52 && bytes[1] === 0x49) return 'image/webp';
  return 'image/svg+xml';
}

// Decodes to ImageData no larger than maxSize on its longest side.
export async function decodeImageBytes(bytes, maxSize = MAX_TIP_SIZE) {
  const dims = readImageDimensions(bytes);
  if (dims && (dims.width > MAX_DECODE_DIMENSION || dims.height > MAX_DECODE_DIMENSION || dims.width * dims.height > MAX_DECODE_PIXELS || !dims.width || !dims.height)) {
    throw new Error(`Image is too large to decode safely (${dims.width}×${dims.height}).`);
  }
  const mime = mimeOf(bytes);
  if (mime === 'image/svg+xml' && bytes.length > 2 * 1024 * 1024) throw new Error('SVG tip is too large.');
  const blob = new Blob([bytes], { type: mime });
  let source = null;
  let width = 0;
  let height = 0;
  if (typeof createImageBitmap === 'function' && mime !== 'image/svg+xml') {
    source = await createImageBitmap(blob);
    width = source.width;
    height = source.height;
  } else {
    const url = URL.createObjectURL(blob);
    try {
      source = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Image could not be decoded.'));
        image.src = url;
      });
      width = source.naturalWidth || source.width || 256;
      height = source.naturalHeight || source.height || 256;
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  }
  if (!width || !height || width * height > MAX_DECODE_PIXELS) throw new Error('Image has invalid dimensions.');
  const scale = Math.min(1, maxSize / Math.max(width, height));
  const canvas = createCanvas(width * scale, height * scale);
  const ctx = context2d(canvas);
  ctx.imageSmoothingEnabled = true;
  if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  if (source && typeof source.close === 'function') source.close();
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

function luminance(data, i) {
  return (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) / 255;
}

function imageStats(imageData) {
  const data = imageData.data;
  let transparent = 0;
  let visible = 0;
  let darkVisible = 0;
  let border = 0;
  let borderLum = 0;
  const { width, height } = imageData;
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    const a = data[i + 3];
    if (a < 250) transparent += 1;
    if (a > 8) {
      visible += 1;
      if (luminance(data, i) < 0.5) darkVisible += 1;
    }
    const x = p % width;
    const y = (p / width) | 0;
    if (x < 2 || y < 2 || x >= width - 2 || y >= height - 2) {
      border += 1;
      borderLum += a > 8 ? luminance(data, i) : 0;
    }
  }
  const total = Math.max(1, width * height);
  return {
    usefulAlpha: transparent / total > 0.02,
    darkVisibleRatio: visible ? darkVisible / visible : 0,
    borderLuminance: border ? borderLum / border : 0,
  };
}

export function resolvePolarity(imageData, polarity) {
  if (polarity === 'light' || polarity === 'dark' || polarity === 'alpha' || polarity === 'image') {
    if (polarity === 'light') {
      const stats = imageStats(imageData);
      // Shapes saved as black-on-transparent would vanish under white = paint.
      if (stats.usefulAlpha && stats.darkVisibleRatio > 0.85) return 'alpha';
    }
    return polarity;
  }
  const stats = imageStats(imageData);
  if (polarity === 'krita') return stats.usefulAlpha && stats.darkVisibleRatio < 0.15 ? 'alpha' : 'dark';
  if (stats.usefulAlpha) return 'alpha';
  return stats.borderLuminance < 0.5 ? 'light' : 'dark';
}

// Mask canvas: alpha = paint coverage, RGB black. 'image' keeps colours.
export function imageDataToTipCanvas(imageData, polarity, { hardEdge = false } = {}) {
  const mode = resolvePolarity(imageData, polarity);
  const canvas = createCanvas(imageData.width, imageData.height);
  const ctx = context2d(canvas);
  const out = ctx.createImageData(imageData.width, imageData.height);
  const src = imageData.data;
  const dst = out.data;
  for (let i = 0; i < src.length; i += 4) {
    const a = src[i + 3] / 255;
    let value;
    if (mode === 'image') {
      dst[i] = src[i];
      dst[i + 1] = src[i + 1];
      dst[i + 2] = src[i + 2];
      dst[i + 3] = src[i + 3];
      continue;
    } else if (mode === 'light') value = luminance(src, i) * a;
    else if (mode === 'dark') value = (1 - luminance(src, i)) * a;
    else value = a;
    if (hardEdge) value = value < 0.5 ? 0 : 1;
    dst[i + 3] = Math.round(Math.max(0, Math.min(1, value)) * 255);
  }
  ctx.putImageData(out, 0, 0);
  return trimCanvas(canvas);
}

function trimCanvas(canvas) {
  const ctx = context2d(canvas);
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  let minX = canvas.width;
  let minY = canvas.height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < canvas.height; y += 1) {
    for (let x = 0; x < canvas.width; x += 1) {
      if (data[(y * canvas.width + x) * 4 + 3] > 2) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return canvas;
  // Keep the dab centred on the original image centre so offsets and
  // rotation behave like the source app: trim symmetrically.
  const cx = (canvas.width - 1) / 2;
  const cy = (canvas.height - 1) / 2;
  const halfW = Math.ceil(Math.max(cx - minX, maxX - cx)) + 2;
  const halfH = Math.ceil(Math.max(cy - minY, maxY - cy)) + 2;
  const width = Math.min(canvas.width, halfW * 2);
  const height = Math.min(canvas.height, halfH * 2);
  if (width >= canvas.width - 2 && height >= canvas.height - 2) return canvas;
  const out = createCanvas(width, height);
  context2d(out).drawImage(canvas, Math.round(cx - width / 2), Math.round(cy - height / 2), width, height, 0, 0, width, height);
  return out;
}

export function grayToImageData(width, height, pixels) {
  const imageData = new ImageData(width, height);
  for (let i = 0; i < width * height; i += 1) {
    imageData.data[i * 4 + 3] = pixels[i];
  }
  return imageData;
}

// --- GIMP brushes ----------------------------------------------------------

export function parseGbrBytes(bytes, offset = 0) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.length < offset + 28) throw new Error('GBR too small');
  const headerSize = view.getUint32(offset, false);
  const version = view.getUint32(offset + 4, false);
  const width = view.getUint32(offset + 8, false);
  const height = view.getUint32(offset + 12, false);
  const depth = view.getUint32(offset + 16, false);
  const spacing = version >= 2 ? view.getUint32(offset + 24, false) : 25;
  if (!width || !height || width > 4096 || height > 4096 || ![1, 2, 3, 4].includes(depth)) throw new Error('Invalid GBR header');
  if (headerSize < 28 || headerSize > 4096) throw new Error('Invalid GBR header size');
  const start = offset + headerSize;
  const length = width * height * depth;
  if (start + length > bytes.length) throw new Error('GBR pixel data is truncated');
  const pixels = bytes.subarray(start, start + length);
  const imageData = new ImageData(width, height);
  const out = imageData.data;
  let colour = false;
  for (let i = 0; i < width * height; i += 1) {
    if (depth === 1) {
      out[i * 4 + 3] = pixels[i];
    } else if (depth === 2) {
      out[i * 4 + 3] = Math.round((pixels[i * 2] * pixels[i * 2 + 1]) / 255);
    } else {
      out[i * 4] = pixels[i * depth];
      out[i * 4 + 1] = pixels[i * depth + 1];
      out[i * 4 + 2] = pixels[i * depth + 2];
      out[i * 4 + 3] = depth === 4 ? pixels[i * 4 + 3] : 255;
      colour = true;
    }
  }
  return { imageData, spacing: spacing / 100, colour, end: start + length };
}

const GIH_SELECTION = { incremental: 'sequential', random: 'random', angular: 'angle', pressure: 'pressure', velocity: 'random', xtilt: 'random', ytilt: 'random' };

export function parseGihBytes(bytes) {
  const firstNewline = bytes.indexOf(10);
  const secondNewline = bytes.indexOf(10, firstNewline + 1);
  if (firstNewline < 0 || secondNewline < 0) throw new Error('Invalid GIH header');
  const params = new TextDecoder('latin1').decode(bytes.subarray(firstNewline + 1, secondNewline));
  const count = Math.min(512, parseInt(params, 10) || 0);
  const selectionMatch = params.match(/sel0:(\w+)/);
  const selection = GIH_SELECTION[selectionMatch ? selectionMatch[1] : 'random'] || 'random';
  const cells = [];
  let offset = secondNewline + 1;
  for (let i = 0; i < count && offset < bytes.length; i += 1) {
    const cell = parseGbrBytes(bytes, offset);
    cells.push(cell);
    offset = cell.end;
  }
  if (!cells.length) throw new Error('GIH contains no brush cells');
  return { cells, selection, rawSelection: selectionMatch ? selectionMatch[1] : '' };
}

// --- Procedural tips -------------------------------------------------------

function erf(x) {
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * ax);
  const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-ax * ax);
  return sign * y;
}

// Krita mask generators (KisCircleMaskGenerator, KisGaussCircleMaskGenerator,
// KisCurveCircleMaskGenerator and rectangle variants). Fade is hfade/vfade.
export function createKritaAutoTip(mask = {}) {
  const diameter = Math.max(2, Math.min(4000, Number(mask.diameter) || 64));
  const ratio = Math.max(0.01, Math.min(1, Number(mask.ratio) || 1));
  const size = Math.max(8, Math.min(512, Math.round(diameter)));
  const width = size;
  const height = Math.max(2, Math.round(size * ratio));
  const canvas = createCanvas(width + 2, height + 2);
  const ctx = context2d(canvas);
  const out = ctx.createImageData(canvas.width, canvas.height);
  const id = String(mask.id || 'default').toLowerCase();
  const rectangle = String(mask.type || 'circle').toLowerCase() === 'rect';
  const hfade = Math.max(0, Math.min(1, Number(mask.hfade) || 0));
  const vfade = Math.max(0, Math.min(1, Number(mask.vfade) || 0));
  const fade = (hfade + vfade) / 2;
  let gauss = null;
  if (id === 'gauss') {
    const f = Math.max(1e-6, 1 - fade);
    const center = (2.5 * (6761 * f - 10000)) / (Math.SQRT2 * 6761 * f);
    gauss = { center, alphaFactor: 1 / (2 * erf(center)), distFactor: (Math.SQRT2 * 12500) / (6761 * f) };
  }
  const cx = (canvas.width - 1) / 2;
  const cy = (canvas.height - 1) / 2;
  for (let y = 0; y < canvas.height; y += 1) {
    for (let x = 0; x < canvas.width; x += 1) {
      const nx = (x - cx) / (width / 2);
      const ny = (y - cy) / (height / 2);
      const n = rectangle ? Math.max(Math.abs(nx), Math.abs(ny)) : Math.sqrt(nx * nx + ny * ny);
      let alpha = 0;
      if (gauss) {
        const d = n * gauss.distFactor;
        alpha = Math.max(0, Math.min(1, gauss.alphaFactor * (erf(d + gauss.center) - erf(d - gauss.center))));
      } else if (n <= 1) {
        if (id === 'soft') {
          const t = 1 - n;
          alpha = t * t * (3 - 2 * t);
        } else {
          const core = 1 - Math.max(0.0001, fade);
          alpha = n <= core ? 1 : Math.max(0, (1 - n) / Math.max(0.0001, 1 - core));
        }
      }
      if (!gauss) alpha = Math.min(1, alpha * Math.min(1, (1 - n) * Math.max(width, height) * 0.5 + 1));
      out.data[(y * canvas.width + x) * 4 + 3] = Math.round(Math.max(0, Math.min(1, alpha)) * 255);
    }
  }
  ctx.putImageData(out, 0, 0);
  return canvas;
}

export function createRoundTip(hardness = 0.8, size = 128) {
  const canvas = createCanvas(size, size);
  const ctx = context2d(canvas);
  const out = ctx.createImageData(size, size);
  const h = Math.max(0, Math.min(1, Number(hardness)));
  const c = (size - 1) / 2;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const r = Math.hypot(x - c, y - c) / (size / 2);
      let alpha = 0;
      if (r <= 1) alpha = h >= 0.999 ? 1 : r <= h ? 1 : Math.max(0, (1 - r) / (1 - h));
      alpha *= Math.min(1, (1 - r) * size * 0.5 + 0.5);
      out.data[(y * size + x) * 4 + 3] = Math.round(Math.max(0, Math.min(1, alpha)) * 255);
    }
  }
  ctx.putImageData(out, 0, 0);
  return canvas;
}

// --- Grain / pattern -------------------------------------------------------

export function imageDataToGrainCanvas(imageData, size = GRAIN_TEXTURE_SIZE) {
  const source = createCanvas(imageData.width, imageData.height);
  context2d(source).putImageData(imageData, 0, 0);
  const canvas = createCanvas(size, size);
  const ctx = context2d(canvas);
  ctx.imageSmoothingEnabled = true;
  if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, size, size);
  ctx.drawImage(source, 0, 0, size, size);
  const data = ctx.getImageData(0, 0, size, size);
  for (let i = 0; i < data.data.length; i += 4) {
    const v = Math.round(luminance(data.data, i) * 255);
    data.data[i] = v;
    data.data[i + 1] = v;
    data.data[i + 2] = v;
    data.data[i + 3] = 255;
  }
  ctx.putImageData(data, 0, 0);
  canvas.grainSourceSize = Math.max(imageData.width, imageData.height);
  return canvas;
}

// --- Entry point -----------------------------------------------------------

function yieldToBrowser() {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

// Returns { tips: [canvas], tipSelection, tipColour, grain, spacing, warnings }.
export async function processCandidateAssets(candidate) {
  const warnings = [];
  const tips = [];
  let tipSelection = 'single';
  let tipColour = (candidate.translation && candidate.translation.settings && candidate.translation.settings.tipColour) || 'mask';
  let spacing = null;
  const hardEdge = !!(candidate.translation && candidate.translation.settings && candidate.translation.settings.edgeMode === 'hard');

  for (const source of candidate.tipSources || []) {
    await yieldToBrowser();
    if (source.kind === 'image') {
      const imageData = await decodeImageBytes(source.bytes);
      const polarity = source.polarity === 'image' ? 'image' : source.polarity;
      tips.push(imageDataToTipCanvas(imageData, polarity, { hardEdge }));
    } else if (source.kind === 'gray') {
      tips.push(imageDataToTipCanvas(grayToImageData(source.width, source.height, source.pixels), 'alpha'));
    } else if (source.kind === 'gbr') {
      const gbr = parseGbrBytes(source.bytes);
      spacing = gbr.spacing;
      if (gbr.colour && source.polarity === 'image') tipColour = 'image';
      tips.push(imageDataToTipCanvas(gbr.imageData, gbr.colour ? (source.polarity === 'image' ? 'image' : 'dark') : 'alpha', { hardEdge }));
    } else if (source.kind === 'gih') {
      const gih = parseGihBytes(source.bytes);
      spacing = gih.cells[0].spacing;
      tipSelection = gih.cells.length > 1 ? gih.selection : 'single';
      const colour = gih.cells.some((cell) => cell.colour);
      if (colour && source.polarity === 'image') tipColour = 'image';
      gih.cells.slice(0, 128).forEach((cell) => tips.push(imageDataToTipCanvas(cell.imageData, colour ? (source.polarity === 'image' ? 'image' : 'dark') : 'alpha', { hardEdge })));
      if (gih.cells.length > 128) warnings.push('Only the first 128 animated tip cells are used.');
    } else if (source.kind === 'procedural') {
      const shape = source.shape || {};
      tips.push(shape.kind === 'krita-auto' ? createKritaAutoTip(shape.mask) : createRoundTip(shape.hardness, 128));
    } else if (source.kind === 'abr-first') {
      warnings.push('ABR tips referenced from Krita presets are not resolved yet; a round tip is used.');
      tips.push(createRoundTip(0.7, 128));
    }
  }

  let grain = null;
  const grainSource = candidate.grainSource || candidate.patternSource;
  if (grainSource && grainSource.bytes) {
    try {
      await yieldToBrowser();
      grain = imageDataToGrainCanvas(await decodeImageBytes(grainSource.bytes, 2048));
    } catch (err) {
      warnings.push('Grain could not be decoded: ' + (err && err.message ? err.message : 'unknown error'));
    }
  }

  return { tips: tips.filter(Boolean), tipSelection, tipColour, grain, spacing, warnings };
}

export function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    if (!canvas || typeof canvas.toBlob !== 'function') {
      reject(new Error('Canvas cannot be encoded.'));
      return;
    }
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Canvas encoding failed.'))), 'image/png');
  });
}

export async function canvasFromBlob(blob, maxSize = 4096) {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const imageData = await decodeImageBytes(bytes, maxSize);
  const canvas = createCanvas(imageData.width, imageData.height);
  context2d(canvas).putImageData(imageData, 0, 0);
  return canvas;
}
